<template>
  <v-dialog :value="getNotifyClerkViewer" persistent scrollable :max-width="dialogMaxWidth" width="104vw"
    max-height="950">
    <v-card class="notify-clerk-card">
      <!-- Close icon -->
      <v-btn icon class="dialog-close-btn" aria-label="Close dialog" @click="closeDialog">
        <v-icon color="error" large>mdi-close</v-icon>
      </v-btn>

      <!-- Show title only after the user selects No attorney -->
      <template v-if="attorneyAnswer === 'no'">
        <v-card-title class="headline notify-header d-flex align-center justify-center">
          Notify Clerk's Office
        </v-card-title>

        <v-divider />
      </template>

      <v-card-text class="notify-clerk-content pt-5 px-6 pb-4">
        <!-- Initial attorney question -->
        <div v-if="attorneyAnswer === null" class="attorney-question-view">
          <div class="attorney-question-panel">
            <div class="question-kicker">
              TRAFFIC COURT CHECK-IN
            </div>

            <div class="question-text">
              Do you have an attorney representing you?
            </div>

            <v-row class="question-options" justify="center">
              <v-col cols="12" sm="6">
                <v-btn block large color="primary" depressed class="question-option-btn"
                  @click="selectAttorneyAnswer('yes')">
                  Yes, I Have an Attorney
                </v-btn>
              </v-col>

              <v-col cols="12" sm="6">
                <v-btn block large color="primary" outlined class="question-option-btn"
                  @click="selectAttorneyAnswer('no')">
                  No, I Do Not Have an Attorney
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Landscape layout after Yes or No is selected -->
        <v-row v-else class="landscape-layout" dense>
          <!-- Main left column -->
          <v-col cols="12" md="8" class="main-column">
            <!-- Attorney is representing the user -->
            <div v-if="attorneyAnswer === 'yes'">
              <v-card outlined class="instruction-box pa-6">
                <div class="text-h5 mb-4 black--text">
                  Please proceed to the respective courtroom(s).
                </div>

                <p class="black--text mb-5">
                  Your attorney will assist you with the hearing process.
                </p>

                <!-- Courtroom 110 plus one or more other courtrooms -->
                <template v-if="hasCourtroom110 && otherCourtroomLocations.length > 0">
                  <p class="black--text mb-3">
                    <strong>
                      Please attend your hearing(s) in the following
                      courtroom(s) first:
                    </strong>
                  </p>

                  <div class="mb-5">
                    <v-chip v-for="courtroom in otherCourtroomLocations" :key="courtroom" class="mr-2 mb-2"
                      color="primary" outlined>
                      Courtroom {{ courtroom }}
                    </v-chip>
                  </div>

                  <p class="black--text mb-3">
                    <strong>
                      After completing those hearing(s), please proceed to
                      Courtroom 110.
                    </strong>
                  </p>

                  <v-chip color="primary" outlined>
                    Courtroom 110
                  </v-chip>
                </template>

                <!-- Only Courtroom 110, or courtrooms other than 110 -->
                <template v-else>
                  <p class="black--text mb-3">
                    <strong>Please proceed to:</strong>
                  </p>

                  <div v-if="courtrooms.length">
                    <v-chip v-for="courtroom in courtrooms" :key="courtroom" class="mr-2 mb-2" color="primary" outlined>
                      Courtroom {{ courtroom }}
                    </v-chip>
                  </div>
                </template>
              </v-card>
            </div>

            <!-- No attorney: check-in and Clerk notification -->
            <div v-else>
              <div class="subtitle-1 mb-4 black--text summary-text">
                You have <strong>{{ hearingCount }}</strong>
                hearing<span v-if="hearingCount !== 1">s</span> scheduled
                today.
              </div>

              <v-row dense>
                <v-col v-for="(hearing, i) in hearings" :key="i" cols="12" sm="6" class="mb-3">
                  <v-card outlined class="hearing-card">
                    <v-card-text class="pa-4 black--text">
                      <div class="hearing-number mb-2">
                        {{ i + 1 }}) Case Number
                        <strong>{{ hearing.caseNumber }}</strong>
                      </div>

                      <div class="hearing-detail mb-1">
                        Courtroom -
                        <strong>
                          {{ normalizeCourtroom(hearing.hearingLocation) }}
                        </strong>
                      </div>

                      <div class="hearing-detail">
                        Time - {{ hearing.time }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Courtroom 110 plus one or more other courtrooms -->
              <div v-if="hasCourtroom110 && otherCourtroomLocations.length > 0"
                class="body-1 black--text instruction-box">
                <p>
                  <strong>Step 1:</strong> Select
                  <strong>“Check-in for Courtroom 110”</strong> below.
                </p>

                <p>
                  <strong>Step 2:</strong> Please proceed to the following
                  courtroom(s) first and attend your hearing(s):
                </p>

                <ul class="mt-0 mb-3">
                  <li v-for="courtroom in otherCourtroomLocations" :key="courtroom">
                    Courtroom <strong>{{ courtroom }}</strong>
                  </li>
                </ul>

                <p>
                  <strong>Step 3:</strong> After completing your hearing(s) in
                  the courtroom(s) above, please proceed to
                  <strong>Courtroom 110</strong> and attend your hearing.
                </p>

                <p class="mt-2 mb-0">
                  I will notify the Clerk that you have checked in, will attend
                  the courtroom(s) listed above first, and will then come to
                  <strong>Courtroom 110</strong>.
                </p>
              </div>

              <!-- User only has Courtroom 110 hearings -->
              <div v-else-if="hasCourtroom110" class="instruction-box">
                <p class="mt-2 mb-0 black--text text-subtitle-1">
                  Please select <strong>“Check-in for Courtroom 110”</strong>,
                  then proceed directly to <strong>Courtroom 110</strong> for
                  your hearing<span v-if="hearingCount !== 1">s</span>.
                </p>
              </div>

              <!-- User has hearings but none are in Courtroom 110 -->
              <div v-else class="instruction-box">
                <p class="mb-3 black--text text-subtitle-1">
                  Please proceed to the following courtroom(s) and attend your
                  hearing(s):
                </p>

                <ul class="mt-0 mb-0">
                  <li v-for="courtroom in otherCourtroomLocations" :key="courtroom">
                    Courtroom <strong>{{ courtroom }}</strong>
                  </li>
                </ul>
              </div>

              <v-alert v-if="statusMessage" :type="statusType" outlined class="mt-4 mb-0">
                {{ statusMessage }}
              </v-alert>
            </div>
          </v-col>

          <!-- Right-side information panel -->
          <v-col cols="12" md="4" class="side-column">
            <v-card outlined class="courtroom-guidelines-card mb-4">
              <v-card-title class="courtroom-guidelines-title">
                Before Entering the Courtroom
              </v-card-title>

              <v-card-text class="pt-2">
                <ul class="courtroom-guidelines-list">
                  <li>Appropriate court attire must be worn.</li>
                  <li>No food, drinks, or gum.</li>
                  <li>No sunglasses.</li>
                  <li>No hats.</li>
                  <li>No tank tops or sleeveless shirts.</li>
                  <li>No reading material.</li>
                  <li>No talking.</li>
                  <li>Do not lean on the bench.</li>
                </ul>
              </v-card-text>
            </v-card>

            <v-card v-if="courtroomDirections.length" outlined class="courtroom-directions-card">
              <v-card-title class="directions-title">
                Directions from This Kiosk
              </v-card-title>

              <v-card-text class="pt-2">
                <div v-for="(item, index) in courtroomDirections" :key="item.courtroom" class="direction-item"
                  :class="{ 'mb-4': index !== courtroomDirections.length - 1 }">
                  <div class="direction-courtroom mb-1">
                    To Courtroom {{ item.courtroom }}
                  </div>

                  <div class="direction-text">
                    {{ item.direction }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons shown only after an attorney choice -->
      <template v-if="attorneyAnswer !== null">
        <v-divider />

        <v-card-actions class="dialog-actions px-5 py-4">
          <!-- Attorney selected Yes -->
          <template v-if="attorneyAnswer === 'yes'">
            <v-btn large color="primary" outlined class="back-btn" @click="returnToAttorneyQuestion">
              Back
            </v-btn>
          </template>

          <!-- Attorney selected No -->
          <template v-else>
            <v-alert v-if="hearingCount > 1 && hasCourtroom110" type="warning" dense border="left" colored-border
              elevation="1" class="other-courtroom-note">
              Please attend your hearings in other courtroom {{ otherCourtroomLocations.toString() }} first and then come to 110 to attend
              your hearing.
            </v-alert>
            <v-btn x-large color="primary" depressed class="check-in-btn" :loading="isSubmitting"
              :disabled="isSubmitting" @click="notifyClerk">
              {{
                hasCourtroom110
                  ? "Check-in for Courtroom 110"
                  : "Check-in for Hearing"
              }}
            </v-btn>
          </template>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { isCheckInWindow } from "@/utils/checkInSchedule";

export default {
  name: "notifyClerkViewer",

  props: {
    clerkEmail: {
      type: String,
      default: "TCCI@lasallecounty.com",
    },

    emailAction: {
      type: String,
      default: "sendEmail",
    },
  },

  data() {
    return {
      attorneyAnswer: null,
      isSubmitting: false,
      statusMessage: "",
      statusType: "error"
    };
  },

  computed: {
    ...mapGetters(["getNotifyClerkViewer", "getSelectedHearing", "getGreetingStatus"]),

    dialogMaxWidth() {
      return this.attorneyAnswer === null ? 780 : 1180;
    },

    hearings() {
      return this.getSelectedHearing.hearings || [];
    },

    hearingCount() {
      return this.hearings.length;
    },

    hasCourtroom110() {
      return this.hearings.some(
        hearing => this.normalizeCourtroom(hearing.hearingLocation) === "110"
      );
    },

    otherCourtrooms() {
      return this.hearings.filter(
        hearing => this.normalizeCourtroom(hearing.hearingLocation) !== "110"
      );
    },


    otherCourtroomLocations() {
      return [
        ...new Set(
          this.otherCourtrooms
            .map(hearing => this.normalizeCourtroom(hearing.hearingLocation))
            .filter(Boolean)
        ),
      ];
    },

    courtrooms() {
      return [
        ...new Set(
          this.hearings
            .map(hearing => this.normalizeCourtroom(hearing.hearingLocation))
            .filter(Boolean)
        ),
      ];
    },

    orderedCourtrooms() {
      if (this.hasCourtroom110 && this.otherCourtroomLocations.length > 0) {
        return [...this.otherCourtroomLocations, "110"];
      }

      return this.courtrooms;
    },

    courtroomDirections() {
      const directions = {
        "110": "Proceed to Courtroom 110 through the door on your right.",
        "210":
          "Proceed to Courtroom 210 by going up the stairs and turning to the right.",
        "223R":
          "Proceed to Courtroom 223R by going up the stairs and turning to the left.",
      };

      return this.orderedCourtrooms
        .map(courtroom => ({
          courtroom,
          direction: directions[courtroom],
        }))
        .filter(item => item.direction);
    },

    checkInLocationLabel() {
      return this.hasCourtroom110
        ? "Courtroom 110"
        : "Scheduled Hearing";
    },

    isUpstairsThen110CheckIn() {
      return (
        this.hasCourtroom110 &&
        this.otherCourtroomLocations.length > 0
      );
    },

    emailCheckInSubjectPrefix() {
      if (this.isUpstairsThen110CheckIn) {
        return "Upstairs Check In";
      }

      if (this.hasCourtroom110) {
        return "110 Check In";
      }

      return `${this.checkInLocationLabel} Check-In`;
    },

    emailNotificationTitle() {
      if (this.isUpstairsThen110CheckIn) {
        return "Upstairs Check-In Notification";
      }

      if (this.hasCourtroom110) {
        return "Courtroom 110 Check-In Notification";
      }

      return `${this.checkInLocationLabel} Check-In Notification`;
    },
  },

  watch: {
    getNotifyClerkViewer(isOpen) {
      if (isOpen) {
        this.resetDialog();

        this.$nextTick(() => {
          //this.speakAvatar(this.getAttorneyQuestionSpeech());
        });
      }
    },
  },

  methods: {
    speakAvatar(message) {
      if (!message) {
        return;
      }

      const speechRequest = this.$store.dispatch("avatarSpeak", message);

      if (speechRequest && typeof speechRequest.catch === "function") {
        speechRequest.catch(() => {
          // Do not interrupt the check-in flow if the avatar is unavailable.
        });
      }
      return true;
    },

    // getAttorneyQuestionSpeech() {
    //   return (
    //     "Do you have an attorney representing you? " +
    //     "Select Yes, I Have an Attorney if you have an attorney representing you. " +
    //     "Select No, I Do Not Have an Attorney if you do not have an attorney and would like to check in with the Clerk's Office."
    //   );
    // },

    formatCourtroomList(courtrooms) {
      const labels = courtrooms
        .filter(Boolean)
        .map(courtroom => `Courtroom ${courtroom}`);

      if (labels.length === 0) {
        return "";
      }

      if (labels.length === 1) {
        return labels[0];
      }

      if (labels.length === 2) {
        return `${labels[0]} and ${labels[1]}`;
      }

      return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]
        }`;
    },

    getAttorneyYesSpeech() {
      const courtroomList = this.formatCourtroomList(this.orderedCourtrooms);

      if (this.hasCourtroom110 && this.otherCourtroomLocations.length > 0) {
        const otherCourtroomList = this.formatCourtroomList(
          this.otherCourtroomLocations
        );

        return (
          "Thank you. Your attorney is representing you. " +
          `Please attend your hearing or hearings in ${otherCourtroomList} first. ` +
          "After completing those hearings, proceed to Courtroom 110. " +
          "Your attorney will assist you with the hearing process."
        );
      }

      return (
        "Thank you. Your attorney is representing you. " +
        `Please proceed to ${courtroomList || "your assigned courtroom"}. ` +
        "Your attorney will assist you with the hearing process."
      );
    },

    getNoAttorneySpeech() {
      const hearingLabel = this.hearingCount === 1 ? "hearing" : "hearings";

      if (this.hasCourtroom110 && this.otherCourtroomLocations.length > 0) {
        const otherCourtroomList = this.formatCourtroomList(
          this.otherCourtroomLocations
        );
        //window.stopSpeech();
        return (
          `You have ${this.hearingCount} ${hearingLabel} scheduled today. ` +
          `Please attend your hearing or hearings in ${otherCourtroomList} first. ` +
          "After completing those hearings, proceed to Courtroom 110. Tap on Check-in for Courtroom 110 below now."
        );
      }

      if (this.hasCourtroom110) {
        //window.stopSpeech();
        return (
          `You have ${this.hearingCount} ${hearingLabel} scheduled today. ` +
          "Select Check-in for Courtroom 110 below, then proceed directly to Courtroom 110 for your hearing. "
        );
      }

      const courtroomList = this.formatCourtroomList(
        this.otherCourtroomLocations
      );
      //window.stopSpeech();
      return (
        `You have ${this.hearingCount} ${hearingLabel} scheduled today. ` +
        "Select Check-in for Hearing below. " +
        `Then proceed to ${courtroomList || "your assigned courtroom"} for your hearing. `
      );
    },

    selectAttorneyAnswer(answer) {
      this.attorneyAnswer = answer;
      this.statusMessage = "";

      this.$nextTick(async () => {
        if (answer === "yes") {
          // Attorney representation is the end of this person's interaction —
          // reset right after they've heard where to go, same as a completed
          // check-in.
          await this.$store.dispatch("avatarSpeak", this.getAttorneyYesSpeech());
          this.closeDialog();
          this.resetSessionAfterCompletion();
        } else {
          this.speakAvatar(this.getNoAttorneySpeech());
        }
      });
    },

    returnToAttorneyQuestion() {
      this.attorneyAnswer = null;
      this.statusMessage = "";

      this.$nextTick(() => {
        //this.speakAvatar(this.getAttorneyQuestionSpeech());
      });
    },

    normalizeCourtroom(value) {
      return String(value || "")
        .trim()
        .toUpperCase()
        .replace(/^(COURTROOM|CR)\s*/i, "");
    },

    resetDialog() {
      this.attorneyAnswer = null;
      this.isSubmitting = false;
      this.statusMessage = "";
      this.statusType = "error";
    },

    finishAfterCheckInSpeech() {
      this.closeDialog();
      this.resetSessionAfterCompletion();
    },

    // Ends the current person's interaction the moment their final
    // confirmation speech is done (check-in completed, or attorney
    // representation acknowledged) — same full reset the normal idle-timeout
    // uses (clears touch/greeting state, closes dialogs, returns to the
    // kiosk's default landing, which is schedule-aware).
    resetSessionAfterCompletion() {
      // Only end the session automatically while the kiosk is actually in
      // check-in mode. Outside a scheduled window, the check-in page is just
      // one more thing reachable from the normal menu, so it shouldn't reset
      // the whole kiosk the moment someone finishes here.
      if (!isCheckInWindow()) {
        return;
      }
      this.$store.commit("setResetKioskStatus", true);
      this.$store.dispatch("kioskStartOver");
    },

    closeDialog() {
      this.$store.commit("setNotifyClerkViewer", false);
      this.resetDialog();
    },

    getClerkEmail() {
      const firstHearing = this.hearings[0] || {};

      return (
        this.getSelectedHearing.clerkEmail ||
        this.getSelectedHearing.clerkEmailAddress ||
        this.getSelectedHearing.notifyClerkEmail ||
        firstHearing.clerkEmail ||
        firstHearing.clerkEmailAddress ||
        this.clerkEmail
      );
    },

    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    buildClerkEmailContent() {
      const partyName = this.escapeHtml(this.getSelectedHearing.partyName);

      const hearingRows = this.hearings
        .map(hearing => {
          return `
            <li>
              <strong>Case Number:</strong> ${this.escapeHtml(
            hearing.caseNumber
          )}<br />
              <strong>Courtroom:</strong> ${this.escapeHtml(
            this.normalizeCourtroom(hearing.hearingLocation)
          )}<br />
              <strong>Time:</strong> ${this.escapeHtml(hearing.time)}
            </li>
          `;
        })
        .join("");

      const upstairsCourtroomList = this.otherCourtroomLocations
        .map(courtroom => {
          return `<li>Courtroom <strong>${this.escapeHtml(
            courtroom
          )}</strong></li>`;
        })
        .join("");

      let checkInMessage = `
        <p>
          <strong>${partyName}</strong> has checked in for
          <strong>${this.escapeHtml(this.checkInLocationLabel)}</strong>.
        </p>
      `;

      if (this.isUpstairsThen110CheckIn) {
        checkInMessage = `
          <p>
            <strong>${partyName}</strong> has checked in today.
          </p>

          <p>
            This person has multiple hearings scheduled today and will attend
            the following courtroom(s) first:
          </p>

          <ul style="padding-left: 20px;">
            ${upstairsCourtroomList}
          </ul>

          <p>
            After completing the hearing(s) listed above, this person will come
            to <strong>Courtroom 110</strong> for their hearing.
          </p>
        `;
      } else if (this.hasCourtroom110) {
        checkInMessage = `
          <p>
            <strong>${partyName}</strong> has checked in for their hearing
            <span>${this.hearingCount !== 1 ? "s" : ""}</span> in
            <strong>Courtroom 110</strong>.
          </p>
        `;
      }

      return `
        <div style="font-family: Arial, sans-serif; color: #1f2937;">
          <h2 style="margin-bottom: 16px;">
            ${this.escapeHtml(this.emailNotificationTitle)}
          </h2>

          ${checkInMessage}

          <p>
            This person has <strong>${this.hearings.length}</strong>
            hearing(s) scheduled today.
          </p>

          <p><strong>Scheduled Hearings:</strong></p>

          <ol style="padding-left: 20px;">
            ${hearingRows}
          </ol>
        </div>
      `;
    },

    async notifyClerk() {
      this.isSubmitting = true;
      this.statusMessage = "";

      const partyName =
        this.getSelectedHearing.partyName || "Hearing Participant";

      let checkInRecorded = false;

      try {
        await this.$store.dispatch("updateKioskUse", {
          name: partyName,
          hearingLocation: this.courtrooms.toString(),
        });

        checkInRecorded = true;

        const clerkEmail = this.getClerkEmail();

        if (!clerkEmail) {
          throw new Error(
            "Check-in was recorded, but no Clerk email address is configured."
          );
        }

        await this.$store.dispatch(this.emailAction, {
          address: clerkEmail,
          subject: `${this.emailCheckInSubjectPrefix} - ${partyName}`,
          content: this.buildClerkEmailContent(),
        });
        this.statusType = "success";
        this.statusMessage =
          "You have been checked in and the Clerk has been notified.";

        // Wait for the avatar to actually finish saying this before closing —
        // avatarSpeak's promise resolves only once this specific speech has
        // played to completion (or been stopped/skipped), never on a guessed
        // delay.
        await this.$store.dispatch("avatarSpeak", this.statusMessage);
        this.finishAfterCheckInSpeech();
      } catch (error) {
        this.statusType = "error";

        this.statusMessage = checkInRecorded
          ? `Your check-in was recorded, but the Clerk email could not be sent. ${error.message || ""
          }`
          : `Unable to complete the check-in. ${error.message || "Please try again."
          }`;

        this.speakAvatar(this.statusMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.notify-clerk-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: min(950px, 90vh);
  border-radius: 16px;
  overflow: hidden;
}

.dialog-close-btn {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 10;
  width: 48px !important;
  height: 48px !important;
  border: 1px solid #f1b5b5;
  border-radius: 50%;
  background: #fff;
}

.dialog-close-btn:hover {
  background: #fff5f5;
}

.notify-clerk-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.notify-header {
  flex: 0 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4ebf3 100%);
  color: #1f2937;
  font-weight: 700;
  padding: 18px 80px 18px 24px;
}

/* Compact initial attorney question */
.attorney-question-view {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 265px;
  padding: 34px 28px 28px;
}

.attorney-question-panel {
  width: 100%;
  max-width: 680px;
  text-align: center;
}

.question-kicker {
  margin-bottom: 12px;
  color: #2c6e8a;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1.5px;
}

.question-text {
  color: #111827;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
}

.question-options {
  margin-top: 26px;
}

.question-option-btn {
  height: 62px !important;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: none;
}

/* Bottom action area */
.dialog-actions {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  background: #fafafa;
}

.other-courtroom-note {
  width: 100%;
  max-width: 560px;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 700;
}

.other-courtroom-note>>>.v-alert__content {
  text-align: center;
}

.check-in-btn {
  min-width: 390px;
  min-height: 72px !important;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.2px;
  text-transform: none;
}

.back-btn {
  min-width: 170px;
  min-height: 52px !important;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  text-transform: none;
}

/* Landscape layout */
.main-column,
.side-column {
  min-width: 0;
}

.summary-text {
  font-size: 19px;
}

.courtroom-guidelines-card,
.courtroom-directions-card {
  border: 1px solid #d7e1ec;
  border-radius: 12px;
  background: #f8fafc;
}

.courtroom-guidelines-title,
.directions-title {
  padding-bottom: 6px;
  color: #1f2937;
  font-size: 19px;
  font-weight: 700;
}

.courtroom-guidelines-list {
  margin: 0;
  padding-left: 28px;
  color: #1f2937;
  font-size: 15px;
  line-height: 1.75;
}

.courtroom-guidelines-list li::marker {
  content: "✓  ";
  font-weight: 700;
}

.hearing-card {
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.hearing-number {
  font-size: 15px;
  line-height: 1.5;
}

.hearing-detail {
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.instruction-box {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  background: #f8fafc;
  line-height: 1.7;
}

.direction-item {
  border-left: 3px solid #2c6e8a;
  padding-left: 12px;
}

.direction-courtroom {
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
}

.direction-text {
  color: #374151;
  font-size: 15px;
  line-height: 1.55;
}

@media (min-width: 960px) {
  .landscape-layout {
    align-items: flex-start;
  }

  .side-column {
    position: sticky;
    top: 0;
  }
}

@media (max-width: 959px) {
  .attorney-question-view {
    min-height: 230px;
    padding: 24px 12px;
  }

  .question-text {
    font-size: 24px;
  }

  .check-in-btn {
    width: 100%;
    min-width: 0;
    font-size: 17px;
  }
}
</style>